/* eslint-disable max-len */
import { CheckCircleFilled } from '@ant-design/icons';
import {
  Button,
  Card, Col, Image, Modal, Row, Space, Spin, Typography
} from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { useAppSelector } from 'app/store';
import { getStaffService } from 'common/services/extends/staff';
import { StaffContractEnum } from 'common/services/extends/staff/types';
import { NoTaxCode } from 'common/utils/constant';
import { formatDateDDMMYYYY, getImageURL } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

interface DetailModalProps {
  openId?: number;
  handleClose(): void;
  handleApprove?: (idx: number) => void;
  handleReject?: (idx: number) => void;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  isApproved?: boolean;
  disabledAction?: boolean;
}
const DetailModal: React.FC<DetailModalProps> = ({
  openId, handleClose, handleApprove, handleReject,
  approveLoading, rejectLoading, isApproved,
  disabledAction
}) => {
  const { t } = useTranslation();
  const { roles: userRoles } = useAppSelector((state) => state.auth);
  const allowApprove = openId && getPermission(userRoles, roles.PROJECT_APPROVED_STAFF);

  const { data: detailData, isLoading: detailLoading } = useQuery(
    ['get-staff-part-time-detail', openId],
    () => getStaffService(openId!),
    {
      enabled: !!openId && getPermission(userRoles, roles.EMPLOYEE_INDEX)
    }
  );

  return (
    <Modal width="60vw" open={!!openId} closable footer={null} onCancel={handleClose}>
      <Spin spinning={detailLoading}>
        <Card
          title={t('employee.information')}
          extra={(
            <Space>
              {!isApproved && (
                <Button type="primary" disabled={!allowApprove} loading={approveLoading} onClick={() => handleApprove?.(openId!)}>
                  {t('program.approveAll')}
                </Button>
              )}
              {
                !disabledAction && (
                  <Button
                    danger
                    loading={rejectLoading}
                    disabled={!allowApprove}
                    onClick={() => Modal.confirm({
                      className: 't-pagetable_deleteRecordModal',
                      autoFocusButton: 'cancel',
                      okText: t('system.ok'),
                      cancelText: t('system.cancel'),
                      cancelButtonProps: {
                        type: 'primary',
                      },
                      okButtonProps: {
                        type: 'default',
                      },
                      title: isApproved ? t('message.confirmDeleteStaff') : t('message.confirmDeleteRecord'),
                      onOk: () => handleReject?.(openId!),
                    })}
                  >
                    {isApproved ? t('program.revoke') : t('program.rejectAll')}
                  </Button>
                )
              }
            </Space>
          )}
          style={{ marginTop: '16px' }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Typography.Text strong>
                {t('employee.fullname')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {detailData?.staffData.fullname}
              </Typography.Text>
            </Col>
            <Col span={8}>
              <Typography.Text strong>
                {t('employee.birthDate')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {formatDateDDMMYYYY(detailData?.staffData.dob, '/')}
              </Typography.Text>
            </Col>
            <Col span={8}>
              <Typography.Text strong>
                {t('employee.taxNumber')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {detailData?.staffData.taxCode || NoTaxCode.label}
              </Typography.Text>
            </Col>
          </Row>
          <Row gutter={16} className="u-mt-8">
            <Col span={8}>
              <Typography.Text strong>
                {t('employee.identityNumber')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {detailData?.staffData.idCardNumber}
              </Typography.Text>
            </Col>
            <Col span={8}>
              <Typography.Text strong>
                {t('employee.grantDate')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {formatDateDDMMYYYY(detailData?.staffData.idCardIssuedAt, '/')}
              </Typography.Text>
            </Col>
            <Col span={8}>
              <Typography.Text strong>
                {t('employee.grantPlace')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {detailData?.staffData.idCardIssuedBy}
              </Typography.Text>
            </Col>
          </Row>
          <Row gutter={16} className="u-mt-8">
            <Col span={24}>
              <Typography.Text strong>
                {t('employee.permanentAddress')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {detailData?.staffData.permanentAddress}
              </Typography.Text>
            </Col>
          </Row>
          <Row gutter={16} className="u-mt-8">
            <Col span={12}>
              <Typography.Text strong>
                {t('employee.idCardFront')}
                :
              </Typography.Text>
              {detailData?.staffData.frontIdCard && (
                <Image.PreviewGroup>
                  <div className="p-programDetail_img">
                    <Image
                      preview
                      alt="frontIdCard"
                      src={getImageURL(detailData.staffData.frontIdCard)}
                    />
                  </div>
                </Image.PreviewGroup>
              )}
            </Col>
            <Col span={12}>
              <Typography.Text strong>
                {t('employee.idCardBack')}
                :
              </Typography.Text>
              {detailData?.staffData.backIdCard && (
                <Image.PreviewGroup>
                  <div className="p-programDetail_img">
                    <Image
                      preview
                      alt="backIdCard"
                      src={
                        getImageURL(detailData.staffData.backIdCard)
                      }
                    />
                  </div>
                </Image.PreviewGroup>
              )}
            </Col>
          </Row>
        </Card>
        <Card title={t('employee.contract')} style={{ marginTop: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Typography.Text strong>
                {t('employee.contractType')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {StaffContractEnum[detailData?.staffData.contractType as number]}
              </Typography.Text>
            </Col>
          </Row>
        </Card>
        <Card title={t('employee.socialInsurance')} style={{ marginTop: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Typography.Text strong>
                {t('employee.socialInsuranceNumber')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {detailData?.staffData.socialInsuranceNumber}
              </Typography.Text>
            </Col>
            <Col span={12}>
              <Typography.Text strong>
                {t('employee.insuranceDate')}
                :
              </Typography.Text>
              <Typography.Text className="u-ml-4">
                {formatDateDDMMYYYY(detailData?.staffData.socialInsuranceJoinDate, '/')}
              </Typography.Text>
            </Col>
          </Row>
          <Row className="u-mt-8">
            <Col span={24}>
              <Typography.Text strong>
                {t('employee.vssidImg')}
                :
              </Typography.Text>
              {detailData?.staffData.socialInsuranceImage && (
                <Image.PreviewGroup>
                  <div className="p-programDetail_img">
                    <Image
                      preview
                      alt="backIdCard"
                      src={
                        getImageURL(detailData.staffData.socialInsuranceImage)
                      }
                    />
                  </div>
                </Image.PreviewGroup>
              )}
            </Col>
          </Row>
        </Card>
        <Card title={t('employee.bank')} style={{ marginTop: '16px' }}>
          {detailData?.bankData && detailData.bankData.length > 0 && (
            detailData.bankData.map((bank, idx) => (
              <Row gutter={16} key={`bank-account-${idx.toString()}`} className="u-mt-12">
                <Col span={24}>
                  <Card>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Typography.Text strong>
                          {t('employee.accountName')}
                          :
                        </Typography.Text>
                        <Typography.Text className="u-ml-4">
                          {bank.accountName}
                        </Typography.Text>
                      </Col>
                      <Col span={8}>
                        <Typography.Text strong>
                          {t('employee.bankAccount')}
                          :
                        </Typography.Text>
                        <Typography.Text className="u-ml-4">
                          {bank.accountNumber}
                        </Typography.Text>
                      </Col>
                      <Col span={8}>
                        <Space>
                          <Typography.Text strong>
                            {t('employee.defaultAccount')}
                            :
                          </Typography.Text>
                          {bank.accountDefault ? <CheckCircleFilled style={{ color: 'green', fontSize: '24px' }} /> : null}
                        </Space>
                      </Col>
                      <Col span={8} className="u-mt-8">
                        <Typography.Text strong>
                          {t('employee.bankName')}
                          :
                        </Typography.Text>
                        <Typography.Text className="u-ml-4">
                          {bank.bankName}
                        </Typography.Text>
                      </Col>
                      <Col span={8} className="u-mt-8">
                        <Typography.Text strong>
                          {t('employee.bankBranch')}
                          :
                        </Typography.Text>
                        <Typography.Text className="u-ml-4">
                          {bank.branchName}
                        </Typography.Text>
                      </Col>
                      <Col span={8} className="u-mt-8">
                        <Typography.Text strong>
                          {t('employee.city')}
                          :
                        </Typography.Text>
                        <Typography.Text className="u-ml-4">
                          {bank.province?.name}
                        </Typography.Text>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            ))
          )}
        </Card>
      </Spin>
    </Modal>
  );
};

export default DetailModal;
